/**
 * The SEO page's client state and its pure transitions: what `useSiteSeo` holds for one casino instance (the last
 * good server state, the local draft, the server's issues and llms preview, the live-meta cache) and how each answer
 * changes it. No React, no request.
 */
import { changedRoutes, documentFromPages, fillGenerated, mergeGenerated } from './seo-document';
import { isWildcardPath } from './route-path';
import type {
  Localized,
  SiteSeoDocument,
  SiteSeoDraftSaved,
  SiteSeoGenerated,
  SiteSeoGenerateField,
  SiteSeoGenerateRequest,
  SiteSeoIssue,
  SiteSeoJob,
  SiteSeoLiveMeta,
  SiteSeoState,
} from './seo-types';

export type SiteSeoStatus = 'idle' | 'loading' | 'ready' | 'unavailable' | 'failed';

export type LiveMetaEntry = { status: 'loading' | 'ready' | 'failed'; value: Localized<SiteSeoLiveMeta> | null };

export interface SiteSeoSnapshot {
  state: SiteSeoState;
  /** The local working document: the server draft (else published, else empty) completed with every page. */
  draft: SiteSeoDocument;
  /** From the state, then replaced by each draft PUT answer (and a publish refusal's issues). */
  issues: SiteSeoIssue[];
  llmsPreview: string;
  /** Live meta per opened path. */
  liveMeta: Record<string, LiveMetaEntry>;
}

/** Completing the draft with the pages is not an edit: nothing is written on load. */
export const snapshotFromState = (state: SiteSeoState): SiteSeoSnapshot => ({
  state,
  draft: documentFromPages(state.draft ?? state.published, state.pages),
  issues: state.issues,
  llmsPreview: state.llms_preview,
  liveMeta: {},
});

/** Something the owner wrote is not published: the same rule as the changed-routes marks and the publish summary, so a
 *  page's empty row (nothing published yet, or a page the site gained) never reads as an unpublished draft. */
export const isDirty = (s: SiteSeoSnapshot): boolean => changedRoutes(s.draft, s.state.published).size > 0;

export const withDraft = (s: SiteSeoSnapshot, draft: SiteSeoDocument): SiteSeoSnapshot => ({ ...s, draft });

/** The PUT answer's issues and llms preview; the draft stays the local one (it may already be newer). */
export const withSaved = (s: SiteSeoSnapshot, saved: SiteSeoDraftSaved): SiteSeoSnapshot => ({
  ...s,
  issues: saved.issues,
  llmsPreview: saved.llms_preview,
});

/** A publish refusal's issues replace the held ones. */
export const withIssues = (s: SiteSeoSnapshot, issues: SiteSeoIssue[]): SiteSeoSnapshot => ({ ...s, issues });

export const withJob = (s: SiteSeoSnapshot, job: SiteSeoJob | null): SiteSeoSnapshot => ({
  ...s,
  state: { ...s.state, publishing: job },
});

export const withLiveMeta = (s: SiteSeoSnapshot, path: string, entry: LiveMetaEntry): SiteSeoSnapshot => ({
  ...s,
  liveMeta: { ...s.liveMeta, [path]: entry },
});

/** Drops the live-meta cache (a finished job changed the site). */
export const withoutLiveMeta = (s: SiteSeoSnapshot): SiteSeoSnapshot => ({ ...s, liveMeta: {} });

export const applyGenerated = (
  s: SiteSeoSnapshot,
  req: SiteSeoGenerateRequest,
  generated: SiteSeoGenerated,
): SiteSeoSnapshot => withDraft(s, mergeGenerated(s.draft, req, generated));

/** Lands the answer only into empty fields (see `fillGenerated`); the same snapshot when nothing landed. */
export const applyGeneratedEmpty = (
  s: SiteSeoSnapshot,
  req: SiteSeoGenerateRequest,
  generated: SiteSeoGenerated,
): { snapshot: SiteSeoSnapshot; landed: SiteSeoGenerateField[] } => {
  const { doc, landed } = fillGenerated(s.draft, req, generated);
  return { snapshot: landed.length ? withDraft(s, doc) : s, landed };
};

/**
 * A good re-read over a held snapshot: everything from the server, the live-meta cache kept, and the local draft kept
 * when `keepDraft` (an unsaved or unpublished local edit the server's draft cannot be newer than).
 */
export const withReloaded = (held: SiteSeoSnapshot, state: SiteSeoState, keepDraft: boolean): SiteSeoSnapshot => {
  const fresh = snapshotFromState(state);
  return { ...fresh, draft: keepDraft ? held.draft : fresh.draft, liveMeta: held.liveMeta };
};

/** Live meta exists for exact and pattern paths only (never a wildcard or `*`). */
export const isLiveMetaPath = (path: string): boolean => path.startsWith('/') && !isWildcardPath(path);

export const isJobOver = (job: SiteSeoJob): boolean => job.status === 'done' || job.status === 'failed';
